import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    CalendarIcon,
    Users,
    Sparkles,
    PartyPopper,
} from "lucide-react";
import { format } from "date-fns";
import { useApp } from "../AppContext";
import api from "../api";
import { toast } from "sonner";
import { FlagStripe } from "./FlagAccents";
import CateringDetailsStep from "./catering/CateringDetailsStep";
import CateringMenuStep from "./catering/CateringMenuStep";
import { StepDot } from "./catering/CateringPrimitives";

const CateringSection = () => {
    const { t, lang, branding, apiBase } = useApp();
    const [step, setStep] = useState(1); // 1 = details, 2 = menu
    const [form, setForm] = useState({
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        event_date: null,
        event_type: "cumpleanos",
        guest_count: 20,
        budget: "",
        notes: "",
    });
    const [menu, setMenu] = useState([]);
    const [menuLoading, setMenuLoading] = useState(false);
    // selections: { item_id: quantity }
    const [selections, setSelections] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [cateringWaUrl, setCateringWaUrl] = useState(null);

    const hasPreorderInSelections = useMemo(() => {
        for (const m of menu) {
            if (selections[m.id] && m.available === false) return true;
        }
        return false;
    }, [menu, selections]);
    const minLeadHours = hasPreorderInSelections ? 72 : 48;
    const minEventDate = useMemo(() => {
        const d = new Date();
        d.setHours(d.getHours() + minLeadHours);
        return d;
    }, [minLeadHours]);

    const onChange = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

    useEffect(() => {
        if (step !== 2 || menu.length > 0) return;
        setMenuLoading(true);
        api.get(`${apiBase}/menu`)
            .then((r) => setMenu(r.data || []))
            .catch(() => setMenu([]))
            .finally(() => setMenuLoading(false));
    }, [step, menu.length, apiBase]);

    const goNext = (e) => {
        e?.preventDefault();
        if (!form.customer_name.trim() || !form.customer_phone.trim() || !form.event_date) {
            toast.error(
                lang === "es"
                    ? "Completa nombre, teléfono y fecha"
                    : "Please fill name, phone and date"
            );
            return;
        }
        setStep(2);
        setTimeout(() => {
            document
                .getElementById("catering")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
    };

    const setQty = (item_id, q) =>
        setSelections((s) => {
            const next = { ...s };
            if (q <= 0) delete next[item_id];
            else next[item_id] = q;
            return next;
        });

    const totalItems = useMemo(
        () => Object.values(selections).reduce((a, b) => a + b, 0),
        [selections]
    );
    const estimated = useMemo(() => {
        let sum = 0;
        for (const m of menu) {
            const q = selections[m.id];
            if (q) sum += m.price * q;
        }
        return sum;
    }, [menu, selections]);

    const recommended = parseInt(form.guest_count) || 0;

    const autoFill = () => {
        const next = {};
        for (const m of menu) {
            if (m.available !== false && m.featured) {
                next[m.id] = recommended;
            }
        }
        setSelections(next);
        toast.success(
            lang === "es"
                ? `Sugerencia: ${Object.values(next).reduce((a, b) => a + b, 0)} porciones`
                : `Suggested: ${Object.values(next).reduce((a, b) => a + b, 0)} portions`
        );
    };

    const submit = async () => {
        if (totalItems === 0) {
            toast.error(
                lang === "es"
                    ? "Selecciona al menos un plato"
                    : "Please pick at least one dish"
            );
            return;
        }
        setSubmitting(true);
        // Open WhatsApp tab synchronously to bypass popup blockers
        const popup = window.open("about:blank", "_blank");
        try {
            const selected_items = Object.entries(selections).map(([id, qty]) => {
                const m = menu.find((x) => x.id === id);
                return {
                    item_id: id,
                    name: m ? (lang === "es" ? m.name_es : m.name_en) : "",
                    price: m ? m.price : 0,
                    quantity: qty,
                };
            });
            const payload = {
                customer_name: form.customer_name,
                customer_phone: form.customer_phone,
                customer_email: form.customer_email || null,
                event_date: format(form.event_date, "yyyy-MM-dd"),
                event_type: form.event_type,
                guest_count: parseInt(form.guest_count) || 0,
                budget: form.budget ? parseFloat(form.budget) : null,
                notes: form.notes || null,
                selected_items,
                estimated_total: estimated,
            };
            const r = await api.post(`${apiBase}/catering`, payload);
            const url = r.data.whatsapp_url;
            setCateringWaUrl(url);
            toast.success(t.catering.success);
            setDone(true);
            if (popup && !popup.closed) {
                try {
                    popup.location.href = url;
                } catch {
                    window.location.href = url;
                }
            } else {
                setTimeout(() => {
                    if (document.visibilityState === "visible") window.location.href = url;
                }, 250);
            }
        } catch (err) {
            if (popup && !popup.closed) popup.close();
            toast.error(err?.response?.data?.detail || t.common.error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="catering"
            data-testid="catering-section"
            className="relative py-24 sm:py-32 bg-earth-50 grain overflow-hidden"
        >
            {branding.decorations_enabled && (
                <FlagStripe thickness="h-1.5" className="absolute top-0 left-0 right-0 opacity-70" />
            )}
            <div
                aria-hidden
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full opacity-10 pointer-events-none"
                style={{ background: "radial-gradient(circle, #002D62 0%, transparent 60%)" }}
            />

            <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="lg:col-span-5 lg:sticky lg:top-32"
                >
                    <span
                        className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-bold border"
                        style={{ borderColor: "#002D62", color: "#002D62", background: "#002D6210" }}
                    >
                        <PartyPopper className="w-3.5 h-3.5" />
                        {t.catering.tag}
                    </span>
                    <h2 className="font-serif font-black text-earth-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5">
                        {t.catering.title}
                    </h2>
                    <p className="mt-5 text-lg text-earth-700 max-w-md">
                        {t.catering.subtitle}
                    </p>

                    {/* Step indicator */}
                    <div
                        className="mt-7 inline-flex items-center gap-3 bg-white border border-earth-200 rounded-full px-4 py-2 shadow-sm"
                        data-testid="catering-step-indicator"
                    >
                        <StepDot active={step === 1} done={step > 1} label="1" />
                        <span className="text-[11px] uppercase tracking-wider font-bold text-earth-700">
                            {lang === "es" ? "Detalles" : "Details"}
                        </span>
                        <div className="w-6 h-px bg-earth-300" />
                        <StepDot active={step === 2} done={false} label="2" />
                        <span className="text-[11px] uppercase tracking-wider font-bold text-earth-700">
                            {lang === "es" ? "Menú" : "Menu"}
                        </span>
                    </div>

                    <div className="mt-8 space-y-3">
                        {[
                            { icon: Users, key: "size",
                                label: lang === "es" ? "Desde 10 hasta 200+ personas" : "From 10 to 200+ people" },
                            { icon: Sparkles, key: "custom",
                                label: lang === "es" ? "Menús personalizados a tu gusto" : "Custom menus tailored to you" },
                            { icon: CalendarIcon, key: "lead",
                                label: lang === "es" ? "Reserva con al menos 5 días" : "Book at least 5 days in advance" },
                        ].map(({ icon: Icon, key, label }) => (
                            <div key={key} className="flex items-center gap-3 text-sm text-earth-700">
                                <div className="w-9 h-9 rounded-full bg-white border border-earth-200 flex items-center justify-center shrink-0">
                                    <Icon className="w-4 h-4 text-terracotta-600" />
                                </div>
                                <span className="font-medium">{label}</span>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="lg:col-span-7 bg-white border border-earth-200 rounded-[32px] p-7 sm:p-10 shadow-md"
                >
                    {step === 1 && (
                        <CateringDetailsStep
                            form={form}
                            onChange={onChange}
                            onSubmit={goNext}
                            minLeadHours={minLeadHours}
                            minEventDate={minEventDate}
                            hasPreorderInSelections={hasPreorderInSelections}
                        />
                    )}

                    {step === 2 && (
                        <CateringMenuStep
                            menu={menu}
                            menuLoading={menuLoading}
                            selections={selections}
                            setQty={setQty}
                            autoFill={autoFill}
                            onBack={() => setStep(1)}
                            onSubmit={submit}
                            submitting={submitting}
                            done={done}
                            cateringWaUrl={cateringWaUrl}
                            form={form}
                            estimated={estimated}
                            totalItems={totalItems}
                            recommended={recommended}
                        />
                    )}
                </motion.div>
            </div>
        </section>
    );
};

export default CateringSection;
